import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import * as Yup from 'yup';

import Checkbox from '../common/Checkbox';
import Editable from '../common/Editable';

import {
  checkIfProductHasVariants,
  getItemDefaultBarcode,
  getItemPriceString,
  getItemVariantsString,
  getProductImageUrl,
  getVariantImageUrl,
  getItemsString,
  getCurrencySymbol,
  getMoneyFormat,
  parseError,
  getType,
  checkIfProductHaveMonoboxes,
  findAllVariants,
  getItemMonoboxesString,
  findAllMonoboxes,
} from '../../helpers';

import { ITEMS_TABLE_THEAD, PRODUCT_BADGES_TYPES, ITEMS_BADGES } from '../../config';
import SpinnerBlock from '../common/SpinnerBlock';
import { toast } from 'react-toastify';
// import { useAutoTranslation } from '../../helpers/useAutoTranslation';
import { useAutoTranslation } from './../../helpers/useAutoTranslation';

const Wrapper = styled('div')`
  .mini-badge {
    height: 19px;
    display: flex;
    width: max-content;
    max-width: 120px;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 11px;
    line-height: 11px;
    margin-bottom: 3px;
  }

  .bs-bars {
    button {
      margin-right: 10px;
    }
  }

  .fixed-table-toolbar {
    min-height: 60px;
  }

  .fixed-table-body {
    overflow-x: auto;
  }

  table.table {
    border-bottom: 1px solid #dee2e6;
    margin-bottom: 0;

    .help-tooltip {
      left: -400px;
    }

    tr {
      &.not-available {
        opacity: 0.4;
      }

      &.variant-row {
        background-color: #fcfdff;

        td {
          padding-left: 30px;
        }
      }

      &.row-has-variants {
        cursor: pointer;
      }
    }

    td {
      vertical-align: middle;
      color: #343a40;

      .variant-input {
        width: 100px;
      }

      .table-item-title {
        font-weight: bold;
      }

      &.chevron-td {
        .fe-chevron-right,
        .fe-chevron-down {
          font-size: 20px;
        }
      }
    }

    th {
      &.both {
        background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABMAAAATCAQAAADYWf5HAAAAkElEQVQoz7X QMQ5AQBCF4dWQSJxC5wwax1Cq1e7BAdxD5SL+Tq/QCM1oNiJidwox0355mXnG/DrEtIQ6azioNZQxI0ykPhTQIwhCR+BmBYtlK7kLJYwWCcJA9M4qdrZrd8pPjZWPtOqdRQy320YSV17OatFC4euts6z39GYMKRPCTKY9UnPQ6P+GtMRfGtPnBCiqhAeJPmkqAAAAAElFTkSuQmCC');
      }

      &.asc {
        background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABMAAAATCAYAAAByUDbMAAAAZ0lEQVQ4y2NgGLKgquEuFxBPAGI2ahhWCsS/gDibUoO0gPgxEP8H4ttArEyuQYxAPBdqEAxPBImTY5gjEL9DM+wTENuQahAvEO9DMwiGdwAxOymGJQLxTyD+jgWDxCMZRsEoGAVoAADeemwtPcZI2wAAAABJRU5ErkJggg==');
      }

      &.desc {
        background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABMAAAATCAYAAAByUDbMAAAAZUlEQVQ4y2NgGAWjYBSggaqGu5FA/BOIv2PBIPFEUgxjB+IdQPwfC94HxLykus4GiD+hGfQOiB3J8SojEE9EM2wuSJzcsFMG4ttQgx4DsRalkZENxL+AuJQaMcsGxBOAmGvopk8AVz1sLZgg0bsAAAAASUVORK5CYII= ');
      }

      &.sortable {
        cursor: pointer;
        background-position: left;
        background-repeat: no-repeat;
        padding-left: 20px;
      }

      &.th-inner {
        vertical-align: bottom;
        text-overflow: ellipsis;
        white-space: nowrap;

        .help-tooltip {
          right: -20px;
          left: auto;
          max-width: 300px;
          white-space: normal;
        }
      }
    }
  }

  .fixed-table-pagination {
    height: 50px;
  }

  .variant-toggle-span {
    font-size: 11px;
    text-decoration: underline;
    color: #aaaaaa;
  }

  .variant-toggle-span-i {
    color: #aaaaaa;
    font-size: 15px;
    margin-left: 3px;
  }

  .variant-id-span,
  .variant-stock-span {
    font-size: 11px;
    color: #98a6ad;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .defective-reserved {
    color: red;
  }

  @media (max-width: 550px) {
    .bs-bars button {
      margin-bottom: 10px;
    }
  }
`;

const SortableTh = (props) => {
  const { id, title, description, sorting, setSorting } = props;

  const { t } = useAutoTranslation();

  let className = 'th-inner sortable both ';
  if (sorting) {
    const { type, direction } = sorting;
    if (type === id) className += direction;
  }
  return (
    <th onClick={() => setSorting({ id })} className={className}>
      {t(title)}
      {description ? (
        <span className="help-tooltip-wrapper">
          <i className="fe-help-circle" />
          <span className="help-tooltip">{description}</span>
        </span>
      ) : null}
    </th>
  );
};

const ItemRow = (props) => {
  const {
    item,
    currencySymbol,
    selected,
    editing,
    hoveredRow,
    toggledItemVariants,
    handleToggleCheckbox,
    handleToggleItemVariants,
    handleToggleItemItems,
    handleItemClick,
    handleEnableTdEditing,
    handleDisableTdEditing,
    handleInputChange,
    handleCancel,
    toggleHoverRowMode,
    toggleRowModeSetOut,
    openVariant,
    urlSearchStr,
    toggledItemItems,
    handleToggleItemMonoboxes,
    toggledItemMonoboxes,
    setCurrentVariantId,
    currentVariantId,
  } = props;

  const hasVariants = checkIfProductHasVariants(item);
  const haveMonoboxes = checkIfProductHaveMonoboxes(item);

  const { t } = useAutoTranslation();

  const handleRowClick = (event) => {
    if (
      event.target.className.indexOf('checkbox-td') !== -1 ||
      event.target.className.indexOf('editable') !== -1
    )
      return false;
    if (hasVariants && item.type !== 'BUNDLE' && item.type !== 'PHYSICAL_SET') {
      handleToggleItemVariants();
    }
    if (
      item.type === 'BUNDLE' ||
      (item.type === 'PHYSICAL_SET' && item.variants[0].items?.length)
    ) {
      handleToggleItemItems();
    }
  };

  let rowClassName = !item.available ? 'not-available ' : '';
  if (hasVariants) rowClassName += 'row-has-variants';

  return (
    <React.Fragment>
      <tr
        className={rowClassName}
        // onMouseEnter={() => toggleHoverRowMode(item.id, 'item')}
        // onMouseLeave={toggleRowModeSetOut}
        onClick={handleRowClick}
      >
        <td className="checkbox-td" onClick={handleToggleCheckbox}>
          <Checkbox id={`checkbox-${item.id}`} checked={selected[item.id]} />
        </td>
        <td style={{ width: '76px' }}>
          <div
            className="image-block"
            style={{ backgroundImage: `url(${getProductImageUrl(item, 100)})` }}
          />
        </td>
        <td style={{ cursor: 'pointer' }} onClick={(e) => handleItemClick(e, item)}>
          {item.type &&
          item.type !== 'SKU' &&
          item.type !== 'PACKING_MATERIAL_BOXES' &&
          item.type !== 'PACKING_MATERIAL_OTHER' &&
          item.type !== 'PACKING_MATERIAL_ATTACHMENT' &&
          item.type !== 'COMMON_PACKING_MATERIAL' ? (
            <span className="badge mini-badge" style={ITEMS_BADGES[getType(item)]}>
              {PRODUCT_BADGES_TYPES.find((t) => t.value === getType(item))?.label.toLowerCase()}
            </span>
          ) : null}
          <span className="table-item-title">{item.shortName}</span>
          {item?.variants?.length === 1 ? (
            <div className="variant-id-span">
              {item?.variants?.[0].id}{' '}
              {item?.variants?.[0]?.extId ? `(${item.variants[0].extId})` : null}
            </div>
          ) : null}
          <br />
          {/* Все варианты вариативного товара */}
          {hasVariants && (
            <>
              <div
                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => {
                  const itemId = item.id;
                  const type = 'variants';
                  handleToggleItemVariants(itemId, type);
                  toggledItemMonoboxes && handleToggleItemMonoboxes(null, null);
                }}
              >
                <span className="variant-toggle-span">
                  {getItemVariantsString(findAllVariants(item.variants).length)}
                </span>
                <i
                  className={`${
                    toggledItemVariants.itemId === item.id &&
                    toggledItemVariants.type === 'variants'
                      ? 'fe-chevron-down'
                      : 'fe-chevron-right'
                  } variant-toggle-span-i`}
                />
              </div>
            </>
          )}

          {/* Все монокороба single product */}
          {haveMonoboxes && !hasVariants && (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: '3px',
                  cursor: 'pointer',
                }}
                onClick={() => handleToggleItemVariants(item.variants[0]?.id, 'monoboxes')}
              >
                <span className="variant-toggle-span">
                  {getItemMonoboxesString(
                    findAllMonoboxes(item.variants, item.variants[0].id).length,
                  )}
                </span>
                <i
                  className={`${
                    toggledItemVariants?.itemId && toggledItemVariants.type === 'monoboxes'
                      ? 'fe-chevron-down'
                      : 'fe-chevron-right'
                  } variant-toggle-span-i`}
                />
              </div>
            </>
          )}

          {(item.type === 'BUNDLE' || item.type === 'PHYSICAL_SET') && item.variants?.[0].items ? (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span className="variant-toggle-span font-weight-bolder">
                {getItemsString(item.variants?.[0]?.items?.length)}
              </span>
              {
                <i
                  className={`${
                    toggledItemItems !== item.id ? 'fe-chevron-right' : 'fe-chevron-down'
                  } variant-toggle-span-i`}
                />
              }
            </div>
          ) : null}
        </td>
        <ItemTd
          editing={editing}
          item={item}
          type="article"
          hoveredRow={hoveredRow}
          placeholder={t('Артикул')}
          handleEnableTdEditing={handleEnableTdEditing}
          handleDisableTdEditing={handleDisableTdEditing}
          handleInputChange={handleInputChange}
          handleCancel={handleCancel}
        />
        <ItemTd item={item} type="barcode" />
        {!hasVariants && !item.variants?.[0]?.autoPriceCalculation ? (
          <ItemTd
            editing={editing}
            item={item}
            currencySymbol={currencySymbol}
            type="price"
            hoveredRow={hoveredRow}
            placeholder={t('Цена')}
            handleEnableTdEditing={handleEnableTdEditing}
            handleDisableTdEditing={handleDisableTdEditing}
            handleInputChange={handleInputChange}
            handleCancel={handleCancel}
          />
        ) : (
          <td>{getItemPriceString(item, null, currencySymbol)}</td>
        )}
        <ItemTd
          editing={editing}
          item={item}
          type="stockAvailable"
          hoveredRow={hoveredRow}
          placeholder={t('Количество')}
          handleEnableTdEditing={handleEnableTdEditing}
          handleDisableTdEditing={handleDisableTdEditing}
          handleInputChange={handleInputChange}
          handleCancel={handleCancel}
        />
        <td></td>
      </tr>

      {/* Варианты - раскрывающийся список */}
      {toggledItemVariants.itemId === item.id
        ? item.variants?.map((variant) => {
            if (!variant.parentPackageId && toggledItemVariants.type === 'variants') {
              return (
                <tr
                  key={variant.id}
                  className="variant-row"
                  // onMouseEnter={() => toggleHoverRowMode(variant.id, 'variant')}
                  // onMouseLeave={toggleRowModeSetOut}
                >
                  <td></td>
                  <td
                    onClick={() => openVariant(item, variant.id)}
                    style={{ cursor: 'pointer', width: '76px' }}
                  >
                    <img
                      src={getVariantImageUrl(variant, 100)}
                      alt={item.shortName}
                      style={{ width: '50px' }}
                    />
                  </td>
                  <td style={{ cursor: 'pointer' }}>
                    {variant.type && variant.type !== 'SKU' ? (
                      <span className="badge mini-badge" style={ITEMS_BADGES[variant.type]}>
                        {PRODUCT_BADGES_TYPES.find(
                          (t) => t.value === variant.type,
                        ).label.toLowerCase()}
                      </span>
                    ) : null}
                    <span onClick={() => openVariant(item, variant.id, urlSearchStr)}>
                      {`${item.shortName}`}
                      {variant?.optionsUsed
                        ? `${variant.optionsUsed
                            .sort((a, b) => (a.code > b.code ? 1 : a.code < b.code ? -1 : 0))
                            .map((o) => o.stringValue)
                            .join(' ')}`
                        : null}
                    </span>
                    <br />
                    <span className="variant-id-span">
                      {variant.id} {variant.extId ? `(${variant.extId})` : null}
                    </span>

                    {/* Монокороба внутри вариантов */}
                    {findAllMonoboxes(item.variants, item.variants[0].id).length > 0 ? (
                      <>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginTop: '3px',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            handleToggleItemMonoboxes(variant.id, 'monoboxes');
                            setCurrentVariantId(variant.id);
                          }}
                        >
                          <span className="variant-toggle-span">
                            {getItemMonoboxesString(
                              findAllMonoboxes(item.variants, variant.id).length,
                            )}
                          </span>
                          {/* <i
                                            className={`${toggledItemVariants.itemId === product.id &&
                                              toggledItemVariants.type === 'monoboxes'
                                              ? 'fe-chevron-down'
                                              : 'fe-chevron-right'
                                              } variant-toggle-span-i`}
                                          /> */}
                          <i
                            className={`${
                              toggledItemMonoboxes?.itemId === variant.id
                                ? 'fe-chevron-down'
                                : 'fe-chevron-right'
                            } variant-toggle-span-i`}
                          />
                        </div>
                      </>
                    ) : null}
                  </td>
                  <ItemTd
                    editing={editing}
                    item={item}
                    variant={variant}
                    type="article"
                    hoveredRow={hoveredRow}
                    placeholder={t('Артикул')}
                    handleEnableTdEditing={handleEnableTdEditing}
                    handleDisableTdEditing={handleDisableTdEditing}
                    handleInputChange={handleInputChange}
                    handleCancel={handleCancel}
                  />
                  <td style={{ padding: `0.85rem` }}>
                    {variant.barcodes && variant.barcodes[0] ? variant.barcodes[0].value : null}
                  </td>
                  <ItemTd
                    editing={editing}
                    item={item}
                    currencySymbol={currencySymbol}
                    variant={variant}
                    type="price"
                    hoveredRow={hoveredRow}
                    placeholder={t('Цена')}
                    handleEnableTdEditing={handleEnableTdEditing}
                    handleDisableTdEditing={handleDisableTdEditing}
                    handleInputChange={handleInputChange}
                    handleCancel={handleCancel}
                  />
                  <ItemTd
                    editing={editing}
                    item={item}
                    variant={variant}
                    type="stockAvailable"
                    hoveredRow={hoveredRow}
                    placeholder={t('Количество')}
                    handleEnableTdEditing={handleEnableTdEditing}
                    handleDisableTdEditing={handleDisableTdEditing}
                    handleInputChange={handleInputChange}
                    handleCancel={handleCancel}
                  />
                  <td />
                </tr>
              );
            }
          })
        : null}

      {/* Монокороба - раскрывающийся список к single product */}
      {toggledItemVariants.itemId && toggledItemVariants.type === 'monoboxes'
        ? findAllMonoboxes(item.variants, toggledItemVariants.itemId)?.map((variant) => {
            return (
              <tr key={variant.id}>
                <td></td>
                <td>
                  <img
                    src={getVariantImageUrl(variant, 100)}
                    alt={variant.shortName}
                    style={{ width: '50px' }}
                  />
                </td>
                <td>
                  <span
                    style={{ cursor: 'pointer' }}
                    onClick={() => openVariant(item, variant.id, true)}
                  >
                    {variant.shortName}
                  </span>
                  <br />
                  <span className="variant-id-span">
                    {variant.id} {variant.extId ? `(${variant.extId})` : null}
                  </span>
                </td>
                <td>{variant.article || ''}</td>
                <td>
                  {getItemDefaultBarcode(variant) ? getItemDefaultBarcode(variant).value : null}
                </td>
                <td>{getItemPriceString(item, variant, currencySymbol)}</td>

                {/* <Stocks
                  variantId={variant.id}
                  stock={variant.stock}
                  currentWarehouse={currentWarehouse}
                  defaultQuantities={defaultVariantQuantities}
                  quantities={variantQuantities}
                /> */}
                <ItemTd
                  editing={editing}
                  item={item}
                  type="stockAvailable"
                  hoveredRow={hoveredRow}
                  placeholder={t('Количество')}
                  handleEnableTdEditing={handleEnableTdEditing}
                  handleDisableTdEditing={handleDisableTdEditing}
                  handleInputChange={handleInputChange}
                  handleCancel={handleCancel}
                  variant={variant}
                />
                {/* <Fragment>
                {needVariants ? (
                  ....
              </Fragment> */}
              </tr>
            );
          })
        : null}

      {/* Монокороба - раскрывающийся список к вариантам (3 уровень) */}
      {toggledItemMonoboxes?.itemId && currentVariantId === toggledItemMonoboxes?.itemId
        ? findAllMonoboxes(item.variants, toggledItemMonoboxes.itemId)?.map((variant) => {
            return (
              <tr key={variant.id} className="variant-row">
                <td></td>
                <td>
                  <img
                    src={getVariantImageUrl(variant, 100)}
                    alt={variant.shortName}
                    style={{ width: '50px' }}
                  />
                </td>
                <td>
                  <span
                    style={{ cursor: 'pointer' }}
                    onClick={() => openVariant(item, variant.id, true)}
                  >
                    {variant.shortName}
                  </span>
                  <br />
                  <span className="variant-id-span">
                    {variant.id} {variant.extId ? `(${variant.extId})` : null}
                  </span>
                </td>
                <td>{variant.article || ''}</td>
                <td>
                  {getItemDefaultBarcode(variant) ? getItemDefaultBarcode(variant).value : null}
                </td>
                <td>{getItemPriceString(item, variant, currencySymbol)}</td>
                <td>
                  <ItemTd
                    editing={editing}
                    item={item}
                    type="stockAvailable"
                    hoveredRow={hoveredRow}
                    placeholder={t('Количество')}
                    handleEnableTdEditing={handleEnableTdEditing}
                    handleDisableTdEditing={handleDisableTdEditing}
                    handleInputChange={handleInputChange}
                    handleCancel={handleCancel}
                  />
                </td>
                {/* <Fragment>
                  {needVariants ? (
                    <td className="chevron-td">
                      {showed[variant.id] ? (
                        <QuantityPicker
                          value={variantQuantities[variant.id]}
                          reverse
                          onChange={(newValue) =>
                            handleChangeVariantQuantity(variant.id, newValue)
                          }
                          hideInputOnBlur={hideInputOnBlur}
                        />
                      ) : (
                        <i
                          className="fe-plus show-quantity-picker"
                          onClick={() => handleChangeShowed(variant.id, true)}
                        />
                      )}
                    </td>
                  ) : (
                    <td />
                  )}
                </Fragment> */}
              </tr>
            );
          })
        : null}

      {toggledItemItems === item.id
        ? item.variants[0].items?.map((elem) => (
            <tr
              key={elem.id}
              className="variant-row"
              onMouseEnter={() => toggleHoverRowMode(elem.id, 'item')}
              onMouseLeave={toggleRowModeSetOut}
            >
              <td></td>
              <td
                onClick={(e) => handleItemClick(e, elem)}
                style={{ cursor: 'pointer', width: '76px' }}
              >
                <img
                  src={getProductImageUrl(elem, 100)}
                  alt={elem.shortName}
                  style={{ width: '50px' }}
                />
              </td>
              <td onClick={(e) => handleItemClick(e, elem)} style={{ cursor: 'pointer' }}>
                <span>{elem.shortName}</span>
                <br />
                <span className="variant-id-span">
                  {elem.variantId} {elem.extId ? `(${elem.extId})` : null}
                </span>
                <br />
                <span className="variant-id-span">
                  Кол-во: {elem.quantity}{' '}
                  {!elem.optional && item.type == 'BUNDLE' ? `(${t('Обязательный')})` : null}
                </span>
              </td>
              <td style={{ padding: `0.85rem` }}>{elem.article}</td>
              <td style={{ padding: `0.85rem` }}>
                {elem.barcodes && elem.barcodes[0] ? elem.barcodes[0].value : null}
              </td>
              <td style={{ padding: `0.85rem` }}>
                {getMoneyFormat(elem.price)} {getCurrencySymbol()}
              </td>
              <ItemTd
                editing={editing}
                item={item.variants[0]}
                variant={elem}
                type="stockAvailable"
                hoveredRow={hoveredRow}
                placeholder={t('Количество')}
                handleEnableTdEditing={handleEnableTdEditing}
                handleDisableTdEditing={handleDisableTdEditing}
                handleInputChange={handleInputChange}
                handleCancel={handleCancel}
              />
            </tr>
          ))
        : null}
    </React.Fragment>
  );
};

const getTrValue = ({ currentInput, child, editing, data }) => {
  return !currentInput ? child || '' : 'value' in editing ? editing.value : data;
};

const ItemTd = (props) => {
  const {
    editing,
    item,
    currencySymbol,
    variant,
    type,
    placeholder = '',
    hoveredRow,
    handleEnableTdEditing,
    handleDisableTdEditing,
    handleInputChange,
    handleCancel,
  } = props;
  let data = {};

  const { t } = useAutoTranslation();

  if (item.variants && item.variants.length && item.variants[0].optionsUsed && !variant) {
    return <td />;
  }

  if (!variant) {
    data =
      item.variants && item.variants.length && !item.variants[0].optionsUsed
        ? item.variants[0]
        : item;
  } else {
    data = variant;
  }

  let defective = null;

  if (data.stock) {
    defective = data.stock.reduce((a, c) => {
      a += c.stockDefective;
      return a;
    }, 0);
  }

  let defectiveReserved = null;

  if (data.stock) {
    defectiveReserved = data.stock.reduce((a, c) => {
      a += c.stockDefectiveReserved;
      return a;
    }, 0);
  }

  let outOfStock = null;

  if (data.stock) {
    outOfStock = data.stock.reduce((a, c) => {
      a += c.outOfStock;
      return a;
    }, 0);
  }

  let stockExpired = null;

  if (data.stock) {
    stockExpired = data.stock.reduce((a, c) => {
      a += c.stockExpired;
      return a;
    }, 0);
  }

  if (type === 'stockAvailable') {
    return (
      <td>
        {data.stock && data.stock.length ? (
          <Fragment>
            {data.stock.reduce((a, c) => {
              a += c.stockAvailable;
              return a;
            }, 0)}
            <br />
            <Link
              to={{
                pathname: '/orders',
                state: {
                  reservedVariantId: data.id,
                  reservedVariantDescription: item.shortName,
                },
              }}
              className="variant-stock-span"
            >
              {`${t('Резерв')}: ${data.stock.reduce((a, c) => {
                a += c.stockReserved;
                return a;
              }, 0)}`}
            </Link>
          </Fragment>
        ) : (
          0
        )}

        {defective ? (
          <Fragment>
            <br />
            <Link
              to={{
                pathname: '/orders',
                state: {
                  reservedVariantId: data.id,
                  reservedVariantDescription: item.shortName,
                },
              }}
              className="variant-stock-span"
            >
              {t('Брак')}: {defective}
            </Link>
          </Fragment>
        ) : null}

        {defectiveReserved ? (
          <Fragment>
            <br />
            <Link
              to={{
                pathname: '/orders',
                state: {
                  reservedVariantId: data.id,
                  reservedVariantDescription: item.shortName,
                },
              }}
              className="variant-stock-span defective-reserved"
            >
              {t('Брак (резерв)')}: {defectiveReserved}
            </Link>
          </Fragment>
        ) : null}

        {outOfStock ? (
          <Fragment>
            <br />
            <Link
              to={{
                pathname: '/orders',
                state: {
                  reservedVariantId: data.id,
                  reservedVariantDescription: item.shortName,
                },
              }}
              className="variant-stock-span"
            >
              Out of stock: {outOfStock}
            </Link>
          </Fragment>
        ) : null}

        {stockExpired ? (
          <Fragment>
            <br />
            <Link
              to={{
                pathname: '/orders',
                state: {
                  reservedVariantId: data.id,
                  reservedVariantDescription: item.shortName,
                },
              }}
              className="variant-stock-span"
            >
              {t('Просрочено')}: {stockExpired}
            </Link>
          </Fragment>
        ) : null}
      </td>
    );
  }

  if (type === 'barcode') {
    const barcode = getItemDefaultBarcode(data);
    return <td>{barcode ? barcode.value : null}</td>;
  }

  let child = data[type];
  let isInvalid = false;
  let currentInput =
    ((editing.id === item.id && !editing.isVariant) ||
      (variant && editing.id === variant.id && editing.isVariant)) &&
    editing.type === type;
  let value = getTrValue({ currentInput, child, editing, data: data[type] });

  if (type === 'price') {
    child = getItemPriceString(item, variant, currencySymbol);
    isInvalid = !Yup.number().moreThan(0).nullable().isValidSync(value);
  }

  return (
    <td>
      <Editable
        disabled={item.options && !variant}
        placeholder={placeholder}
        value={value}
        isInvalid={isInvalid}
        currentInput={
          currentInput ||
          ((variant ? hoveredRow === variant.id : hoveredRow === item.id) &&
            !getTrValue({ currentInput, child, editing, data }))
        }
        preventFocus={!currentInput && !getTrValue({ currentInput, child, editing, data })}
        toggleInput={() => {
          handleEnableTdEditing(type, variant);
        }}
        handleUpdate={() => handleDisableTdEditing(type, variant)}
        onChange={(e) => handleInputChange(e, type, variant)}
        handleCancel={handleCancel}
      />
    </td>
  );
};

export default class TableView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      editing: {},
      sorting: {
        type: 'name',
        direction: 'asc',
      },
      hoveredRow: null,
      toggledItemVariants: {
        itemId: null,
        type: null,
      },
      toggledItemMonoboxes: {
        itemId: null,
        type: null,
      },
      toggledItemItems: null,
      currentVariantId: null,
    };
  }

  prepareData() {
    let newData = this.props.data;
    return newData;
  }

  checkActualSelected() {
    const { selected } = this.state;
    const data = this.prepareData();
    return Object.keys(selected).reduce((a, id) => {
      if (selected[id] && data.find((item) => item.id === id)) {
        a[id] = true;
      }
      return a;
    }, {});
  }

  clearSelected() {
    this.setState({ selected: {}, selectedAll: false });
  }

  handleSorting({ id }) {
    const { getItems, updateSorting = () => false, updatedSorting } = this.props;
    const type = id;
    const direction =
      id === updatedSorting?.type ? (updatedSorting?.direction === 'desc' ? 'asc' : 'desc') : 'asc';
    this.setState({
      sorting: {
        type,
        direction,
      },
    });
    updateSorting({
      type,
      direction,
    });
  }

  handleEnableTdEditing(item, type, variant) {
    const { id } = item;
    const { editing } = this.state;
    if (!variant) {
      if (item.options) return false;
      if (editing.id === id && editing.type === type && !editing.isVariant) return false;
      this.setState({ editing: { id, type, isVariant: false } });
    } else {
      if (editing.id === variant.id && editing.type === type && editing.isVariant) return false;
      this.setState({ editing: { id: variant.id, type, isVariant: true } });
    }
  }

  async handleDisableTdEditing({ item, type, shouldSave, variant }) {
    const { editing } = this.state;
    const { fetchEditVariant, updateProduct = () => false } = this.props;

    const product = Object.assign({}, item);
    if (product.collections) delete product.collections;

    if (shouldSave) {
      if (!variant) {
        if (product.variants && product.variants.length) {
          try {
            await fetchEditVariant({
              ...product.variants[0],
              [type]: editing.value,
              productId: product.id,
              variantId: product.variants[0].id,
            });
          } catch (err) {
            toast.error(parseError(err));
          }
        }
      } else {
        try {
          await fetchEditVariant({
            ...variant,
            [type]: editing.value,
            productId: product.id,
            variantId: variant.id,
          });
        } catch (err) {
          toast.error(parseError(err));
        }
      }
      updateProduct(item.id);
    }
    this.setState({ editing: {} });
  }

  handleItemClick(e, item) {
    if (item.type === 'COMMON_PACKING_MATERIAL') {
      return false;
    }

    if (
      e.target.className.indexOf('variant-toggle-span') !== -1 ||
      e.target.className.indexOf('variant-toggle-span-i') !== -1 ||
      e.target.className.indexOf('checkbox-td') !== -1
    ) {
      if (
        (item.type === 'BUNDLE' || item.type === 'PHYSICAL_SET') &&
        item.variants[0].items?.length
      ) {
        this.handleToggleItemItems(item.id);
        return false;
      }
      if (item.type !== 'BUNDLE' && item.type !== 'PHYSICAL_SET') {
        this.handleToggleItemVariants(item.id);
        return false;
      }
    }
    const { onItemClick } = this.props;
    onItemClick(item);
  }

  handleToggleItemVariants = (itemId, type) => {
    if (itemId === undefined || type === undefined) {
      return;
    }

    this.setState((prevState) => ({
      toggledItemVariants:
        prevState.toggledItemVariants.itemId !== itemId
          ? { itemId, type }
          : {
              itemId: type === prevState.toggledItemVariants.type ? null : itemId,
              type,
            },
    }));
  };

  handleToggleItemMonoboxes = (itemId, type) => {
    if (itemId === undefined || type === undefined) {
      return;
    }
    this.setState((prevState) => ({
      toggledItemMonoboxes:
        prevState.toggledItemMonoboxes.itemId !== itemId
          ? { itemId, type }
          : {
              itemId: type === prevState.toggledItemMonoboxes.type ? null : itemId,
              type,
            },
    }));
  };

  handleToggleItemItems(itemId) {
    const { toggledItemItems } = this.state;
    this.setState({ toggledItemItems: toggledItemItems !== itemId ? itemId : null });
  }

  toggleHoverRowMode(itemId, type) {
    const { hoveredRow } = this.state;
    if (itemId !== hoveredRow) {
      this.setState({ hoveredRow: itemId });
    }
  }

  handleInputChange(e, type, item, variant) {
    this.setState({
      editing: {
        id: !variant ? item.id : variant.id,
        type,
        isVariant: Boolean(variant),
        value: e.target.value,
      },
    });
  }

  setCurrentVariantId = (id) => {
    this.setState({
      currentVariantId: id,
    });
  };

  render() {
    const {
      currencySymbol,
      selected,
      selectedAll,
      handleToggleMainCheckbox,
      handleToggleCheckbox,
      openVariant,
      urlSearchStr,
      skuSets,
      loading,
      updatedSorting,
      t,
    } = this.props;

    const { toggledItemVariants, editing, hoveredRow, toggledItemItems, toggledItemMonoboxes } =
      this.state;

    const data = this.prepareData();

    return (
      <Fragment>
        <Wrapper className="bootstrap-table">
          <div className="fixed-table-container">
            <div className="fixed-table-body">
              <table className="table table-centered">
                <thead className="thead-light">
                  <tr>
                    <th
                      style={{ width: '20px' }}
                      className="checkbox-td"
                      onClick={handleToggleMainCheckbox}
                    >
                      <Checkbox id={`checkbox-all`} checked={selectedAll} />
                    </th>
                    <th style={{ width: '76px' }}></th>
                    {ITEMS_TABLE_THEAD.map((th) => (
                      <SortableTh
                        key={th.id}
                        id={th.id}
                        title={th.title}
                        description={t(th.description)}
                        sorting={updatedSorting}
                        setSorting={(e) => this.handleSorting(e)}
                      />
                    ))}
                    <th />
                  </tr>
                </thead>

                <tbody>
                  {!loading &&
                    data.map((item) => (
                      <ItemRow
                        key={item.id}
                        item={item}
                        currencySymbol={currencySymbol}
                        selected={selected}
                        editing={editing}
                        hoveredRow={hoveredRow}
                        toggledItemVariants={toggledItemVariants}
                        toggledItemMonoboxes={toggledItemMonoboxes}
                        toggledItemItems={toggledItemItems}
                        toggleHoverRowMode={(itemId, type) => this.toggleHoverRowMode(itemId, type)}
                        toggleRowModeSetOut={() => this.toggleHoverRowMode(null)}
                        handleToggleCheckbox={() => handleToggleCheckbox(item.id)}
                        handleToggleItemVariants={this.handleToggleItemVariants}
                        handleToggleItemMonoboxes={this.handleToggleItemMonoboxes}
                        handleToggleItemItems={() => this.handleToggleItemItems(item.id)}
                        handleItemClick={(e, item) => this.handleItemClick(e, item)}
                        handleEnableTdEditing={(type, variant) =>
                          this.handleEnableTdEditing(item, type, variant)
                        }
                        handleDisableTdEditing={(type, variant) =>
                          this.handleDisableTdEditing({
                            item,
                            type,
                            shouldSave: 'value' in editing,
                            variant,
                          })
                        }
                        handleInputChange={(e, type, variant) =>
                          this.handleInputChange(e, type, item, variant)
                        }
                        handleCancel={() => this.setState({ editing: {} })}
                        openVariant={openVariant}
                        urlSearchStr={urlSearchStr}
                        skuSets={skuSets}
                        setCurrentVariantId={this.setCurrentVariantId}
                        currentVariantId={this.state.currentVariantId}
                      />
                    ))}
                </tbody>
              </table>
              {loading ? (
                <div
                  style={{ width: '100%', position: 'relative', height: '150px ' }}
                  className="d-flex justify-content-center"
                >
                  <SpinnerBlock show={loading} />
                </div>
              ) : null}
            </div>
          </div>
        </Wrapper>
      </Fragment>
    );
  }
}
